import type { FC } from 'react'
import styled from '@emotion/styled'

import { AppearanceProvider, useAppearance } from '@segment/matcha/foundations/AppearanceContext'

import Image from '@segment/matcha/components/Image'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import type { AppearanceProps } from '@segment/matcha/types'

import aiAdoptionGraph from './assets/AiAdoptionGraph.png'
import aiAdoptionGraphWebp from './assets/AiAdoptionGraph.webp'
import aiAdoptionGraphWebp2x from './assets/AiAdoptionGraph@2x.webp'

const copy = {
  heading: `Over 70% of brands agree that AI adoption will fundamentally change personalization and marketing strategies.`,
  description: [
    `These leaders understand that AI's ability to process massive amounts of data in real-time will enable more predictive insights and customized approaches, fundamentally shifting how we interact with customers.
    `,
    `This shift will bring personalization to new heights, as companies use AI to anticipate needs, craft tailored experiences, and build deeper, more meaningful connections with their audience.`,
  ],
}

const AiAdoption: FC<AppearanceProps> = ({ appearance: externalAppearance }) => {
  const appearance = useAppearance(externalAppearance)

  return (
    <AppearanceProvider appearance={appearance}>
      <Wrapper>
        <SectionComponent variant="2Col">
          <Section.Copy alignSelf="center">
            <CopyTextBase>
              <CopyText.Heading tag="h2">{copy.heading}</CopyText.Heading>
              {copy.description.map((text, index) => (
                <CopyText.Description key={index} tag="p">
                  {text}
                </CopyText.Description>
              ))}
            </CopyTextBase>
          </Section.Copy>
          <Section.Contents>
            <picture>
              <source type="image/webp" srcSet={`${aiAdoptionGraphWebp}, ${aiAdoptionGraphWebp2x} 2x`} />
              <Image width="512" height="392" src={aiAdoptionGraph} alt={`Ai Adoption Graph Image`} />
            </picture>
          </Section.Contents>
        </SectionComponent>
      </Wrapper>
    </AppearanceProvider>
  )
}

const SectionComponent = styled(SectionBase)`
  overflow-x: visible;
  justify-content: center;
  ${Section.Wrapper} {
    ${({ theme }) => theme.breakpoints.down(`md`)} {
      grid-template-areas:
        'contents'
        'copy';
    }
  }
`

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.palette.twilio.gray[100]};
`

export default AiAdoption
